import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n";

import { listUnifiedCatalog } from "@/lib/catalog.functions";
import {
  STATE_CATALOG,
  STATE_SLUGS,
  productsForState,
  matchProducts, normalize,
} from "@/lib/tour-search";
import { SEARCH_COPY } from "@/lib/search-options";
import { correctQuery } from "@/lib/search-fuzzy";
import { EXPLORA_DESTINATIONS, countToursForDestination } from "@/lib/explora-destinations";
import heroImage from "@/assets/hero-chichen-dusk.jpg.asset.json";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { TourAutocomplete } from "@/components/site/TourAutocomplete";

const catalogQuery = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified", lang],
    queryFn: () => listUnifiedCatalog({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/destinos/")({
  head: () => ({
    meta: [
      { title: "Destinos en México por estado — Mostrando México" },
      {
        name: "description",
        content:
          "Explora tours y experiencias en México agrupados por entidad federativa: Quintana Roo, Yucatán, CDMX, Oaxaca, Jalisco y más.",
      },
      { property: "og:title", content: "Destinos de México — Mostrando México" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: heroImage.url },
      { name: "twitter:image", content: heroImage.url },
      {
        property: "og:description",
        content: "Tours organizados por entidad federativa.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/destinos" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(catalogQuery(context.lang)),
  component: DestinosPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</div>
  ),
  notFoundComponent: () => (
    <div className="p-8 text-center text-sm text-muted-foreground">No encontrado.</div>
  ),
});

function DestinosPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Suspense fallback={<div className="h-screen" />}>
        <DestinosBody />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function DestinosBody() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { data } = useSuspenseQuery(catalogQuery(lang));
  const [search, setSearch] = useState("");
  const [heroFailed, setHeroFailed] = useState(false);

  const tours = data.tours;
  const destinations = useMemo(() => {
    const query = correctQuery(search);
    return EXPLORA_DESTINATIONS.filter(d => !query || query.split(" ").every(token => normalize(`${d.name} ${d.state} ${d.keywords.join(" ")}`).includes(token)))
      .map(d => ({ ...d, count: countToursForDestination(d,tours) })).sort((a,b) => b.count-a.count);
  }, [search, tours]);

  const stateStats = useMemo(() => {
    return STATE_SLUGS.map((slug) => ({
      slug,
      entry: STATE_CATALOG[slug],
      count: productsForState(tours, slug).length,
    })).sort((a, b) => b.count - a.count);
  }, [tours]);

  const filtered = useMemo(() => {
    if (!search.trim()) return stateStats;
    const matched = matchProducts(tours, search);
    const ids = new Set(matched.map((p) => p.id));
    return stateStats
      .map((s) => ({
        ...s,
        count: productsForState(tours, s.slug).filter((p) => ids.has(p.id)).length,
      }))
      .filter((s) => s.count > 0 || normalize(`${s.entry.name} ${s.entry.terms.join(" ")}`).includes(correctQuery(search)));
  }, [stateStats, tours, search]);

  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-border bg-surface">
        {/* Foto del hero. Si la imagen no carga, el degradado de marca queda
            visible detrás y el texto sigue siendo legible (fallback seguro). */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/20 via-surface to-accent/40"
        >
          {!heroFailed && (
            <img
              src={heroImage.url}
              alt=""
              loading="eager"
              decoding="async"
              onError={() => setHeroFailed(true)}
              className="h-full w-full object-cover object-center"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/85 to-background/45" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            {t("destinosPage.destinosTitle")}
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            {t("destinosPage.destinosSubtitle")}
          </p>

          <div className="mt-6 flex h-14 items-center gap-3 rounded-2xl border border-border bg-background px-4 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <TourAutocomplete
              products={tours}
              value={search}
              onChange={setSearch}
              onSubmit={setSearch}
              placeholder={t("catalogPage.searchPlaceholder")}
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {filtered.length === 0 && destinations.length > 0 ? null : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <p className="font-display text-xl">{t("destinosPage.noDestinations")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(({ slug, entry, count }) => (
              <Link
                key={slug}
                to="/destinos/$state"
                params={{ state: slug }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition hover:border-primary hover:shadow-lg"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                  <MapPin className="h-3.5 w-3.5" /> {t("catalogPage.stateLabel")}
                </div>
                <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight group-hover:text-primary">
                  {entry.name}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                  {t(`destTagline.${slug}`, { defaultValue: entry.tagline })}
                </p>
                <p className="mt-4 text-sm font-semibold text-foreground/80">
                  {count}{" "}
                  {count === 1
                    ? t("catalogPage.availableOne")
                    : t("catalogPage.availableMany")}
                </p>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-3xl font-semibold">
              {t("destinosPage.guidesTitle")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("destinosPage.guidesSubtitle")}
            </p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {destinations.map((d) => (
            <Link
              key={d.slug}
              to="/destino/$slug"
              params={{ slug: d.slug }}
              className="group block overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary hover:shadow-md"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={d.image}
                  alt={d.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <p className="font-display text-lg font-semibold leading-tight group-hover:text-primary">
                  {d.name}
                </p>
                <p className="text-xs text-muted-foreground">{d.state}</p>
                <p className="mt-1 text-xs text-muted-foreground">{d.count > 0 ? `${d.count} ${SEARCH_COPY[lang].count}` : SEARCH_COPY[lang].coming}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
